import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
  PieChart,
  Pie,
  Legend,
} from "recharts";
import {
  Download,
  Calendar,
  ChevronDown,
  AlertCircle,
} from "lucide-react";
import { apiClient } from "../../apiclient/apiclient";
import Loader from "../../components/Loader";
import ThemedTable from "../../components/ThemedTable";
import ExcelJS from "exceljs";

// ─── Constants ────────────────────────────────────────────────────────────────

const PERIODS = [
  { label: "This Month",   value: "this_month" },
  { label: "Last Month",   value: "last_month" },
  { label: "This Quarter", value: "this_quarter" },
  { label: "This Year",    value: "this_year" },
];

const fmt = (n) => "₹ " + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

// Helper to resolve period value to YYYY-MM-DD date range
const getPeriodDates = (periodVal) => {
  const today = new Date();
  let start = new Date();
  let end = new Date();

  if (periodVal === "this_month") {
    start = new Date(today.getFullYear(), today.getMonth(), 1);
    end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  } else if (periodVal === "last_month") {
    start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    end = new Date(today.getFullYear(), today.getMonth(), 0);
  } else if (periodVal === "this_quarter") {
    const quarter = Math.floor(today.getMonth() / 3);
    start = new Date(today.getFullYear(), quarter * 3, 1);
    end = new Date(today.getFullYear(), (quarter + 1) * 3, 0);
  } else if (periodVal === "this_year") {
    start = new Date(today.getFullYear(), 0, 1);
    end = new Date(today.getFullYear(), 11, 31);
  }

  const fmtYMD = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  return { start: fmtYMD(start), end: fmtYMD(end) };
};

// Helper to format YYYY-MM-DD to DD-MM-YYYY
const formatYMDtoDMY = (ymdStr) => {
  if (!ymdStr) return "";
  const parts = ymdStr.split("-");
  if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
  return ymdStr;
};

// ─── Main Component ───────────────────────────────────────────────────────────

const SalesSummary = () => {
  const navigate = useNavigate();
  const [period, setPeriod]         = useState("this_month");
  const [periodOpen, setPeriodOpen] = useState(false);
  const [loading, setLoading]       = useState(false);
  const [allOrders, setAllOrders]   = useState([]);
  const [clients, setClients]       = useState([]);

  const selectedLabel = PERIODS.find((p) => p.value === period)?.label;

  // 1. Fetch Sales and Clients from APIs
  const fetchSalesSummary = async () => {
    try {
      setLoading(true);
      const currentYear = new Date().getFullYear();
      // Fetch B2B & POS orders (current year) to prevent timeout hangs
      const salesRes = await apiClient.get(`/daily-sales?start=${currentYear}-01-01&end=${currentYear}-12-31&lite=true`);
      setAllOrders(salesRes.data.table || []);

      // Fetch B2B clients outstanding ledgers
      const clientsRes = await apiClient.get("/clients?lite=true&balance=true");
      setClients(clientsRes.data.clients || []);
    } catch (err) {
      console.error("Failed to load sales summary data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalesSummary();
  }, []);

  const orders = useMemo(() => {
    const { start, end } = getPeriodDates(period);
    const startTime = new Date(start).getTime();
    const endTime = new Date(end).setHours(23, 59, 59, 999);
    return allOrders.filter((o) => {
      const orderTime = new Date(o.createdAt || o.date).getTime();
      return orderTime >= startTime && orderTime <= endTime;
    });
  }, [allOrders, period]);

  // Helper to compute tax and base for an order item
  const getItemTaxAndBase = (item) => {
    const qty = item.quantity || 0;
    const price = item.price || 0;
    const rate = item.gst || 0; // Fixed key to item.gst
    const isTaxInclusive = item.taxInclude ?? true;
    const lineTotal = price * qty;

    let base = 0;
    let tax = 0;

    if (isTaxInclusive) {
      base = Number(((lineTotal * 100) / (100 + rate)).toFixed(4));
      tax = Number((lineTotal - base).toFixed(4));
    } else {
      base = Number(lineTotal.toFixed(4));
      tax = Number((lineTotal * (rate / 100)).toFixed(4));
    }

    if (Math.abs(tax) < 0.0001) tax = 0;
    if (Math.abs(base) < 0.0001) base = 0;

    return { base, tax, rate };
  };

  // ─── Computed Values ───────────────────────────────────────────────────────

  // KPI Calculations
  const calculatedKPIs = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const yearStart = new Date(currentYear, 0, 1).getTime();
    const yearEnd = new Date(currentYear, 11, 31, 23, 59, 59, 999).getTime();

    // 1. Filter orders for this year
    const thisYearOrders = allOrders.filter((o) => {
      const orderTime = new Date(o.createdAt || o.date).getTime();
      return orderTime >= yearStart && orderTime <= yearEnd;
    });

    // This Year Calculations
    const thisYearSales = thisYearOrders.reduce((sum, o) => sum + Number(o.totalAmount || 0), 0);
    const thisYearB2BSales = thisYearOrders
      .filter((o) => o.orderType !== "POS")
      .reduce((sum, o) => sum + Number(o.totalAmount || 0), 0);
    const thisYearPOSSales = thisYearOrders
      .filter((o) => o.orderType === "POS")
      .reduce((sum, o) => sum + Number(o.totalAmount || 0), 0);

    const thisYearCollection = thisYearOrders.reduce((sum, o) => sum + Number(o.paidAmount || 0), 0);
    let thisYearGST = 0;
    let thisYearTaxable = 0;
    const thisYearSlabs = {};
    thisYearOrders.forEach((o) => {
      (o.items || []).forEach((item) => {
        const { tax, base, rate } = getItemTaxAndBase(item);
        thisYearGST += tax;
        thisYearTaxable += base;
        if (tax > 0) {
          thisYearSlabs[rate] = (thisYearSlabs[rate] || 0) + tax;
        }
      });
    });

    // All-Time Outstanding
    const allTimeOutstanding = clients.reduce((sum, c) => sum + Math.max(Number(c.balance || 0), 0), 0);

    // Period-Specific Calculations (Dynamic)
    const periodSales = orders.reduce((sum, o) => sum + Number(o.totalAmount || 0), 0);
    const periodB2BSales = orders
      .filter((o) => o.orderType !== "POS")
      .reduce((sum, o) => sum + Number(o.totalAmount || 0), 0);
    const periodPOSSales = orders
      .filter((o) => o.orderType === "POS")
      .reduce((sum, o) => sum + Number(o.totalAmount || 0), 0);

    const periodCollection = orders.reduce((sum, o) => sum + Number(o.paidAmount || 0), 0);
    let periodGST = 0;
    let periodTaxable = 0;
    const periodSlabs = {};
    orders.forEach((o) => {
      (o.items || []).forEach((item) => {
        const { tax, base, rate } = getItemTaxAndBase(item);
        periodGST += tax;
        periodTaxable += base;
        if (tax > 0) {
          periodSlabs[rate] = (periodSlabs[rate] || 0) + tax;
        }
      });
    });

    // Round slab maps
    Object.keys(thisYearSlabs).forEach((rate) => {
      thisYearSlabs[rate] = Number(thisYearSlabs[rate].toFixed(2));
    });
    Object.keys(periodSlabs).forEach((rate) => {
      periodSlabs[rate] = Number(periodSlabs[rate].toFixed(2));
    });

    return {
      thisYearSales: Number(thisYearSales.toFixed(2)),
      thisYearB2BSales: Number(thisYearB2BSales.toFixed(2)),
      thisYearPOSSales: Number(thisYearPOSSales.toFixed(2)),
      thisYearTaxable: Number(thisYearTaxable.toFixed(2)),
      thisYearGST: Number(thisYearGST.toFixed(2)),
      thisYearCollection: Number(thisYearCollection.toFixed(2)),
      thisYearSlabs,
      allTimeOutstanding: Number(allTimeOutstanding.toFixed(2)),
      periodSales: Number(periodSales.toFixed(2)),
      periodB2BSales: Number(periodB2BSales.toFixed(2)),
      periodPOSSales: Number(periodPOSSales.toFixed(2)),
      periodTaxable: Number(periodTaxable.toFixed(2)),
      periodGST: Number(periodGST.toFixed(2)),
      periodCollection: Number(periodCollection.toFixed(2)),
      periodSlabs,
    };
  }, [allOrders, orders, clients]);

  // Payment Breakdown (using flat paidAmount vs balanceAmount split)
  const paymentMethodsData = useMemo(() => {
    let totalPaid = 0;
    let totalCredit = 0;

    orders.forEach((o) => {
      totalPaid += Number(o.paidAmount || 0);
      totalCredit += Number(o.balanceAmount || 0);
    });

    return [
      { name: "Received (Cash/Online)", value: totalPaid, color: "#172554" },
      { name: "Client Credit", value: totalCredit, color: "#f43f5e" },
    ].filter(m => m.value > 0); // Hide empty slices
  }, [orders]);

  // Monthly Sales vs GST (Bar Chart)
  const monthlyChartData = useMemo(() => {
    const monthlyMap = {};

    orders.forEach((o) => {
      const dateObj = new Date(o.createdAt || o.date);
      const monthName = dateObj.toLocaleString("default", { month: "short" });
      const sortKey = dateObj.getFullYear() * 12 + dateObj.getMonth();

      if (!monthlyMap[monthName]) {
        monthlyMap[monthName] = { month: monthName, sales: 0, tax: 0, sortKey };
      }

      monthlyMap[monthName].sales += o.totalAmount || 0; // Fixed key from paymentDetails.totalAmount

      (o.items || []).forEach((item) => {
        const { tax } = getItemTaxAndBase(item);
        monthlyMap[monthName].tax += tax;
      });
    });

    return Object.values(monthlyMap).sort((a, b) => a.sortKey - b.sortKey);
  }, [orders]);



  // B2B Accounts Receivable list (Top 10 only)
  const receivablesData = useMemo(() => {
    return clients
      .filter((c) => Number(c.balance || 0) > 0)
      .map((c) => {
        const lastPaidDate = c.lastOrderedDate ? new Date(c.lastOrderedDate) : null;
        const formattedLastPaid = lastPaidDate 
          ? `${String(lastPaidDate.getDate()).padStart(2, "0")}-${String(lastPaidDate.getMonth() + 1).padStart(2, "0")}-${lastPaidDate.getFullYear()}` 
          : "N/A";

        return {
          client: c.name || "N/A",
          pending: c.balance,
          lastPaid: formattedLastPaid,
          status: Number(c.balance || 0) > 15000 ? "Overdue" : "Active",
        };
      })
      .sort((a, b) => b.pending - a.pending) // Highest pending first
      .slice(0, 10);
  }, [clients]);

  // Tax Breakdown slabs
  const taxBreakdownData = useMemo(() => {
    const slabs = {};

    orders.forEach((o) => {
      (o.items || []).forEach((item) => {
        const { base, tax, rate } = getItemTaxAndBase(item);
        if (!slabs[rate]) {
          slabs[rate] = { rate: `GST ${rate}%`, sales: 0, tax: 0 };
        }
        slabs[rate].sales += base;
        slabs[rate].tax += tax;
      });
    });

    return Object.values(slabs).map((slab) => ({
      ...slab,
      sales: Number(slab.sales.toFixed(2)),
      tax: Number(slab.tax.toFixed(2)),
    })).sort((a, b) => b.sales - a.sales);
  }, [orders]);

  // ─── Excel Export Function ──────────────────────────────────────────────────

  const handleExportExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet("Sales Summary");

      sheet.views = [{ showGridLines: true }];

      // Merging headers boundary is 6 columns (A to F)
      const rangeLabel = `${formatYMDtoDMY(getPeriodDates(period).start)} to ${formatYMDtoDMY(getPeriodDates(period).end)}`;

      // ── Header Banner
      sheet.mergeCells("A1:F1");
      const titleCell = sheet.getCell("A1");
      titleCell.value = "SALES SUMMARY & REVENUE REPORT";
      titleCell.font = { size: 12, bold: true, color: { argb: "FFFFFFFF" } };
      titleCell.alignment = { horizontal: "center", vertical: "middle" };
      titleCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF172554" },
      };
      sheet.getRow(1).height = 24;

      // ── Period Banner
      sheet.mergeCells("A2:F2");
      const periodCell = sheet.getCell("A2");
      periodCell.value = `Period: ${rangeLabel}`;
      periodCell.font = { size: 11, italic: true, color: { argb: "FFFFFFFF" } };
      periodCell.alignment = { horizontal: "center", vertical: "middle" };
      periodCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF172554" },
      };
      sheet.getRow(2).height = 20;

      // Spacer
      sheet.addRow([]);

      // ── Executive Summary Section
      const startSummaryRow = 4;
      sheet.mergeCells(startSummaryRow, 1, startSummaryRow, 6);
      const summaryHeader = sheet.getCell(startSummaryRow, 1);
      summaryHeader.value = "EXECUTIVE FINANCIAL SUMMARY";
      summaryHeader.font = { size: 10, bold: true, color: { argb: "FFFFFFFF" } };
      summaryHeader.alignment = { horizontal: "center", vertical: "middle" };
      summaryHeader.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF1B365D" },
      };
      sheet.getRow(startSummaryRow).height = 26;

      const summaryRows = [
        { type: "title", text: "REVENUE & TAX PERFORMANCE" },
        { type: "metric", label: "Gross Revenue (B2B + POS)", value: calculatedKPIs.periodSales, format: '"₹"#,##0.00' },
        { type: "metric", label: "Taxes Collected (GST)", value: calculatedKPIs.periodGST, format: '"₹"#,##0.00' },
        { type: "metric", label: "Net Sales (Before Taxes)", value: calculatedKPIs.periodTaxable, format: '"₹"#,##0.00' },
        { type: "metric", label: "Total Collections (Paid Amount)", value: calculatedKPIs.periodCollection, format: '"₹"#,##0.00' },
        { type: "metric", label: "Outstanding Credit Balance", value: calculatedKPIs.allTimeOutstanding, format: '"₹"#,##0.00' },
      ];

      summaryRows.forEach((item, idx) => {
        const rNum = startSummaryRow + (idx + 1);
        sheet.getRow(rNum).height = 20;

        if (item.type === "title") {
          sheet.mergeCells(rNum, 1, rNum, 6);
          const cell = sheet.getCell(rNum, 1);
          cell.value = item.text;
          cell.font = { size: 9, bold: true, color: { argb: "FF1E293B" } };
          cell.alignment = { horizontal: "center", vertical: "middle" };

          for (let c = 1; c <= 6; c++) {
            const tc = sheet.getCell(rNum, c);
            tc.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF1F5F9" } };
            tc.border = {
              top: { style: "thin", color: { argb: "FFCBD5E1" } },
              bottom: { style: "thin", color: { argb: "FFCBD5E1" } },
            };
          }
        } else if (item.type === "metric") {
          sheet.mergeCells(rNum, 1, rNum, 3);
          const lblCell = sheet.getCell(rNum, 1);
          lblCell.value = item.label;
          lblCell.font = { size: 9, color: { argb: "FF475569" }, bold: true };
          lblCell.alignment = { horizontal: "left", vertical: "middle" };

          sheet.mergeCells(rNum, 4, rNum, 6);
          const valCell = sheet.getCell(rNum, 4);
          valCell.value = item.value;
          valCell.font = { size: 9, color: { argb: "FF1E293B" }, bold: true };
          valCell.alignment = { horizontal: "right", vertical: "middle" };
          if (item.format) valCell.numFormat = item.format;

          for (let c = 1; c <= 6; c++) {
            sheet.getCell(rNum, c).border = {
              bottom: { style: "thin", color: { argb: "FFE2E8F0" } },
            };
          }
        }
      });

      // Spacer Row
      const spacerIndex = startSummaryRow + summaryRows.length + 1;
      sheet.addRow([]);

      // ── Tax Breakdown section
      const taxHeaderIndex = spacerIndex + 1;
      sheet.mergeCells(taxHeaderIndex, 1, taxHeaderIndex, 6);
      const taxHeader = sheet.getCell(taxHeaderIndex, 1);
      taxHeader.value = "TAX (GST) BREAKDOWN SUMMARY";
      taxHeader.font = { size: 10, bold: true, color: { argb: "FFFFFFFF" } };
      taxHeader.alignment = { horizontal: "center", vertical: "middle" };
      taxHeader.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF1B365D" },
      };
      sheet.getRow(taxHeaderIndex).height = 26;

      const taxThRow = sheet.addRow(["Tax Slab", "", "Taxable Value", "", "GST Collected", ""]);
      taxThRow.height = 22;
      [1, 3, 5].forEach((cIdx) => {
        sheet.mergeCells(taxThRow.number, cIdx, taxThRow.number, cIdx + 1);
        const cell = sheet.getCell(taxThRow.number, cIdx);
        cell.font = { size: 9, bold: true, color: { argb: "FF1E293B" } };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF1F5F9" } };
      });

      taxBreakdownData.forEach((row) => {
        const itemRow = sheet.addRow([row.rate, "", row.sales, "", row.tax, ""]);
        itemRow.height = 20;

        sheet.mergeCells(itemRow.number, 1, itemRow.number, 2);
        const cell1 = sheet.getCell(itemRow.number, 1);
        cell1.font = { size: 9, color: { argb: "FF475569" } };
        cell1.alignment = { horizontal: "left", vertical: "middle" };

        sheet.mergeCells(itemRow.number, 3, itemRow.number, 4);
        const cell3 = sheet.getCell(itemRow.number, 3);
        cell3.value = row.sales;
        cell3.font = { size: 9, color: { argb: "FF1E293B" } };
        cell3.alignment = { horizontal: "right", vertical: "middle" };
        cell3.numFormat = '"₹"#,##0.00';

        sheet.mergeCells(itemRow.number, 5, itemRow.number, 6);
        const cell5 = sheet.getCell(itemRow.number, 5);
        cell5.value = row.tax;
        cell5.font = { size: 9, color: { argb: "FF1E293B" }, bold: true };
        cell5.alignment = { horizontal: "right", vertical: "middle" };
        cell5.numFormat = '"₹"#,##0.00';

        for (let c = 1; c <= 6; c++) {
          sheet.getCell(itemRow.number, c).border = {
            bottom: { style: "thin", color: { argb: "FFE2E8F0" } },
          };
        }
      });

      // Auto-fit Columns A-F
      sheet.columns.forEach((column) => {
        column.width = 18;
      });

      // Generate Download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `SalesSummaryReport_${period.toUpperCase()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to generate Sales Summary Excel:", err);
      alert("Failed to export report.");
    }
  };

  // ─── Render View ───────────────────────────────────────────────────────────

  return (
    <>
      <div className="bg-slate-50 min-h-screen">
        
        {/* ── HEADER ── */}
        <div className="w-full border-b pt-3 ps-4 pb-4 border-slate-200 bg-blue-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <h1 className="text-xl font-bold text-slate-800">Sales Summary</h1>
            <p className="text-sm text-slate-500 mt-1">
              Financial overview of revenue, taxes, and collections
            </p>
          </div>
          <div className="flex items-center gap-3 pe-4">
            {/* Period Selector */}
            <div className="relative">
              <button
                onClick={() => setPeriodOpen((o) => !o)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50 shadow-sm transition"
              >
                <Calendar size={15} className="text-slate-400" />
                {selectedLabel}
                <ChevronDown size={14} className="text-slate-400" />
              </button>
              {periodOpen && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-100 rounded-xl shadow-lg z-10 w-44 overflow-hidden">
                  {PERIODS.map((p) => (
                    <button
                      key={p.value}
                      onClick={() => { setPeriod(p.value); setPeriodOpen(false); }}
                      className={`w-full text-left px-4 py-2.5 text-sm transition hover:bg-gray-50 ${
                        period === p.value ? "text-blue-700 font-semibold bg-blue-50" : "text-gray-700"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={handleExportExcel}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-md text-sm font-medium hover:bg-orange-600 transition"
            >
              <Download size={15} />
              Export
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-40">
            <Loader size={36} />
            <p className="text-sm text-slate-400 mt-4">Generating financial summaries...</p>
          </div>
        ) : (
          <>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5">
              {/* This Year Sales (Total) */}
              <div className="relative p-4 text-white overflow-hidden bg-gradient-to-br from-blue-500 to-indigo-600 border-e border-b flex flex-col justify-between min-h-[115px]">
                <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40" />
                <p className="text-xs opacity-90 relative z-10 font-bold uppercase tracking-wider">Total Sales</p>
                <h2 className="text-xl md:text-2xl font-bold mt-1 relative z-10 whitespace-nowrap">{fmt(calculatedKPIs.thisYearSales)}</h2>
                <p className="text-[10px] opacity-75 relative z-10 font-semibold uppercase tracking-wider mt-1">This Year</p>
              </div>

              {/* This Year B2B Sales */}
              <div className="relative p-4 text-white overflow-hidden bg-gradient-to-br from-purple-500 to-indigo-700 border-e border-b flex flex-col justify-between min-h-[115px]">
                <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40" />
                <p className="text-xs opacity-90 relative z-10 font-bold uppercase tracking-wider">B2B Sales</p>
                <h2 className="text-xl md:text-2xl font-bold mt-1 relative z-10 whitespace-nowrap">{fmt(calculatedKPIs.thisYearB2BSales)}</h2>
                <p className="text-[10px] opacity-75 relative z-10 font-semibold uppercase tracking-wider mt-1">This Year</p>
              </div>

              {/* This Year POS Sales */}
              <div className="relative p-4 text-white overflow-hidden bg-gradient-to-br from-orange-500 to-amber-600 border-e border-b flex flex-col justify-between min-h-[115px]">
                <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40" />
                <p className="text-xs opacity-90 relative z-10 font-bold uppercase tracking-wider">POS Sales</p>
                <h2 className="text-xl md:text-2xl font-bold mt-1 relative z-10 whitespace-nowrap">{fmt(calculatedKPIs.thisYearPOSSales)}</h2>
                <p className="text-[10px] opacity-75 relative z-10 font-semibold uppercase tracking-wider mt-1">This Year</p>
              </div>

              {/* This Year Taxable Sales */}
              <div className="relative p-4 text-white overflow-hidden bg-gradient-to-br from-teal-500 to-emerald-600 border-e border-b flex flex-col justify-between min-h-[115px]">
                <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40" />
                <p className="text-xs opacity-90 relative z-10 font-bold uppercase tracking-wider">Taxable Sales</p>
                <h2 className="text-xl md:text-2xl font-bold mt-1 relative z-10 whitespace-nowrap">{fmt(calculatedKPIs.thisYearTaxable)}</h2>
                <p className="text-[10px] opacity-75 relative z-10 font-semibold uppercase tracking-wider mt-1">This Year</p>
              </div>

              {/* This Year GST */}
              <div className="relative p-4 text-white overflow-hidden bg-gradient-to-br from-rose-500 to-red-600 border-b flex flex-col justify-between min-h-[115px]">
                <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40" />
                <p className="text-xs opacity-90 relative z-10 font-bold uppercase tracking-wider">GST</p>
                <h2 className="text-xl md:text-2xl font-bold mt-1 relative z-10 whitespace-nowrap">{fmt(calculatedKPIs.thisYearGST)}</h2>
                <p className="text-[10px] opacity-75 relative z-10 font-semibold uppercase tracking-wider mt-1">This Year</p>
              </div>
            </div>


            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 bg-white border-b border-slate-200">
              {/* Period Total Sales */}
              <div className="py-4 px-4 border-b sm:border-e border-slate-200 flex flex-col justify-between min-h-[115px]">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                  Total Sales
                </p>
                <h3 className="text-xl md:text-2xl font-bold text-slate-800 mt-1 font-mono whitespace-nowrap">
                  {fmt(calculatedKPIs.periodSales)}
                </h3>
                <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider mt-1">{selectedLabel}</p>
              </div>

              {/* Period B2B Sales */}
              <div className="py-4 px-4 border-b sm:border-e border-slate-200 flex flex-col justify-between min-h-[115px]">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                  B2B Sales
                </p>
                <h3 className="text-xl md:text-2xl font-bold text-slate-800 mt-1 font-mono whitespace-nowrap">
                  {fmt(calculatedKPIs.periodB2BSales)}
                </h3>
                <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider mt-1">{selectedLabel}</p>
              </div>

              {/* Period POS Sales */}
              <div className="py-4 px-4 border-b sm:border-e border-slate-200 flex flex-col justify-between min-h-[115px]">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                  POS Sales
                </p>
                <h3 className="text-xl md:text-2xl font-bold text-slate-800 mt-1 font-mono whitespace-nowrap">
                  {fmt(calculatedKPIs.periodPOSSales)}
                </h3>
                <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider mt-1">{selectedLabel}</p>
              </div>

              {/* Period Taxable Sales */}
              <div className="py-4 px-4 border-b sm:border-e border-slate-200 flex flex-col justify-between min-h-[115px]">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                  Taxable Sales
                </p>
                <h3 className="text-xl md:text-2xl font-bold text-slate-800 mt-1 font-mono whitespace-nowrap">
                  {fmt(calculatedKPIs.periodTaxable)}
                </h3>
                <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider mt-1">{selectedLabel}</p>
              </div>

              {/* Period Taxes GST */}
              <div className="py-4 px-4 flex flex-col justify-between min-h-[115px]">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                  Taxes GST
                </p>
                <h3 className="text-xl md:text-2xl font-bold text-slate-800 mt-1 font-mono whitespace-nowrap">
                  {fmt(calculatedKPIs.periodGST)}
                </h3>
                <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider mt-1">{selectedLabel}</p>
              </div>
            </div>

            {/* ── CHARTS SIDE BY SIDE ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Payment breakdown Pie Chart */}
              <div className="bg-white border-b border-e p-4 flex flex-col">
                <div className="flex items-center justify-between mb-6 px-2">
                  <h3 className="text-sm font-bold text-[#172554] uppercase tracking-wider">Payment Mode Breakdown</h3>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{selectedLabel}</span>
                </div>
                <div className="h-[280px]">
                  {paymentMethodsData.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-sm text-slate-400">No collections recorded in this period.</div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={paymentMethodsData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={48}>
                          {paymentMethodsData.map((entry, index) => (
                            <Cell key={`pie-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          content={({ active, payload }) => {
                            if (!active || !payload?.length) return null;
                            return (
                              <div className="bg-[#172554] text-white rounded-lg px-4 py-3 shadow-2xl border border-white/10">
                                <p className="text-[10px] font-bold opacity-50 uppercase tracking-widest mb-1">{payload[0].name}</p>
                                <p className="text-base font-black">{fmt(payload[0].value)}</p>
                              </div>
                            );
                          }}
                        />
                        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              {/* Sales vs Tax Bar Chart */}
              <div className="bg-white border-b p-4 flex flex-col">
                <div className="flex items-center justify-between mb-6 px-2">
                  <h3 className="text-sm font-bold text-[#172554] uppercase tracking-wider">Sales vs GST Collected</h3>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-[#172554]" /><span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Sales</span></div>
                    <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-[#F69314]" /><span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Tax</span></div>
                  </div>
                </div>
                <div className="h-[280px]">
                  {monthlyChartData.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-sm text-slate-400">No monthly metrics available in this period.</div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={monthlyChartData} barCategoryGap="25%">
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                        <XAxis dataKey="month" tick={{ fontSize: 9, fill: "#94a3b8", fontWeight: 600 }} axisLine={{ stroke: "#f1f5f9" }} tickLine={false} dy={8} />
                        <YAxis tick={{ fontSize: 10, fill: "#94a3b8", fontWeight: 600 }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                        <Tooltip
                          cursor={{ fill: "#f8fafc" }}
                          content={({ active, payload }) => {
                            if (!active || !payload?.length) return null;
                            return (
                              <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-2xl">
                                <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">{payload[0].payload.month}</p>
                                <p className="text-base font-black text-[#172554]">Sales: {fmt(payload[0].value)}</p>
                                <p className="text-base font-black text-[#F69314] mt-0.5">GST: {fmt(payload[1].value)}</p>
                              </div>
                            );
                          }}
                        />
                        <Bar dataKey="sales" name="Sales" fill="#172554" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="tax" name="Tax" fill="#F69314" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </div>

            {/* GST Summary Table (Full Width) */}
            <div className="bg-white border-b">
              <div className="px-5 py-4 border-b border-slate-200">
                <h3 className="text-sm font-bold text-[#172554] uppercase tracking-wider">Tax (GST) Summary</h3>
                <p className="text-xs text-slate-400 mt-0.5">Taxable value and GST breakdown for {selectedLabel}</p>
              </div>
              <div className="overflow-x-auto">
                <ThemedTable className="w-full text-[13px]">
                  <thead>
                    <tr>
                      <th className="text-left px-5 py-3 text-xs uppercase tracking-wider">Tax Slab</th>
                      <th className="text-right px-5 py-3 text-xs uppercase tracking-wider">Taxable Sales</th>
                      <th className="text-right px-5 py-3 text-xs uppercase tracking-wider">GST Collected</th>
                      <th className="text-right px-5 py-3 text-xs uppercase tracking-wider">Total Sales</th>
                    </tr>
                  </thead>
                  <tbody>
                    {taxBreakdownData.length === 0 ? (
                      <tr><td colSpan={4} className="text-center py-8 text-slate-400">No GST transactions in this period.</td></tr>
                    ) : (
                      taxBreakdownData.map((row, i) => (
                        <tr key={i}>
                          <td className="font-medium text-slate-700">{row.rate}</td>
                          <td className="text-right text-slate-600">{fmt(row.sales)}</td>
                          <td className="text-right font-semibold text-slate-800">{fmt(row.tax)}</td>
                          <td className="text-right text-slate-800 font-semibold">{fmt(row.sales + row.tax)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {taxBreakdownData.length > 0 && (
                    <tfoot>
                      <tr className="border-t-2 border-[#172554]/20 bg-blue-50">
                        <td className="px-5 py-3 font-black text-[#172554] text-sm">Total</td>
                        <td className="px-5 py-3 text-right font-black text-[#172554]">{fmt(taxBreakdownData.reduce((s, r) => s + r.sales, 0))}</td>
                        <td className="px-5 py-3 text-right font-black text-[#172554]">{fmt(taxBreakdownData.reduce((s, r) => s + r.tax, 0))}</td>
                        <td className="px-5 py-3 text-right font-black text-[#172554]">{fmt(taxBreakdownData.reduce((s, r) => s + r.sales + r.tax, 0))}</td>
                      </tr>
                    </tfoot>
                  )}
                </ThemedTable>
              </div>
            </div>

            {/* Accounts Receivable Table (Top 10 - Full Width) */}
            <div className="bg-white border-b">
              <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold text-[#172554] uppercase tracking-wider">Accounts Receivable (Top 10)</h3>
                  <p className="text-xs text-slate-400 mt-0.5">Highest outstanding credit balances</p>
                </div>
                <button 
                  onClick={() => navigate("/books/ledger-collections")}
                  className="text-xs font-bold text-blue-900 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded transition"
                >
                  View All
                </button>
              </div>
              <div className="overflow-x-auto">
                <ThemedTable className="w-full text-[13px]">
                  <thead>
                    <tr>
                      <th className="text-left px-5 py-3 text-xs uppercase tracking-wider">Client Name</th>
                      <th className="text-right px-5 py-3 text-xs uppercase tracking-wider">Outstanding</th>
                      <th className="text-left px-5 py-3 text-xs uppercase tracking-wider">Last Activity</th>
                      <th className="text-center px-5 py-3 text-xs uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receivablesData.length === 0 ? (
                      <tr><td colSpan={4} className="text-center py-8 text-slate-400">No outstanding receivables found.</td></tr>
                    ) : (
                      receivablesData.map((row, i) => (
                        <tr key={i}>
                          <td className="font-medium text-slate-800">{row.client}</td>
                          <td className="text-right font-bold text-red-600">{fmt(row.pending)}</td>
                          <td className="text-slate-500">{row.lastPaid}</td>
                          <td className="text-center">
                            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-red-100 text-red-700`}>
                              <AlertCircle size={10} />
                              {row.status}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </ThemedTable>
              </div>
            </div>
          </>
        )}

      </div>
    </>
  );
};

export default SalesSummary;
